/**
 * ShortLinkPage.tsx
 * 短链跳转页面
 *
 * 功能:
 * - 解析短链代码
 * - 跳转到真实分享页面
 */

import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { shareApi } from '@/services/api';
import { Loader2, AlertCircle } from 'lucide-react';

export default function ShortLinkPage() {
  const { shortCode } = useParams<{ shortCode: string }>();
  const navigate = useNavigate();
  const [error, setError] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!shortCode) {
      setError('短链代码不能为空');
      setIsLoading(false);
      return;
    }

    // 解析短链
    shareApi.resolveShortCode(shortCode)
      .then((res) => {
        const shareId = res.data.data?.shareId;
        if (shareId) {
          // 跳转到真实分享页面
          navigate(`/share/${shareId}`, { replace: true });
        } else {
          setError('短链无效');
          setIsLoading(false);
        }
      })
      .catch((err) => {
        const message = err.response?.data?.error?.message || '短链解析失败';
        setError(message);
        setIsLoading(false);
      });
  }, [shortCode, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">正在解析短链...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4 max-w-md px-4">
          <AlertCircle className="h-12 w-12 text-red-500" />
          <h1 className="text-xl font-semibold">短链访问失败</h1>
          <p className="text-muted-foreground text-center">{error}</p>
          <button
            onClick={() => navigate('/')}
            className="mt-4 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            返回首页
          </button>
        </div>
      </div>
    );
  }

  return null;
}
